/**
 * CheckAddEngineFields — presentational sub of `CheckAddFormBody` covering
 * the per-engine extras (mssql WITH NOCHECK checkbox, postgres NO INHERIT
 * checkbox, mysql NOT ENFORCED checkbox + 8.0.16+ note, oracle DEFERRABLE
 * radios). Orchestrator owns state; this component renders the input controls
 * and propagates change events through setters.
 *
 * Split from `CheckAddFormBody` per the 200-line file budget (Slice B3 split
 * pattern).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B4.
 */
import { Checkbox } from '../../../../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';

export type DeferrableMode = 'unspecified' | 'deferred' | 'immediate';

export interface CheckAddEngineFieldsProps {
  readonly testId: string;
  readonly showWithNoCheck: boolean;
  readonly showNoInherit: boolean;
  readonly showNotEnforced: boolean;
  readonly showDeferrable: boolean;
  readonly mysqlVersionNote: string | null;
  readonly withNoCheck: boolean;
  readonly noInherit: boolean;
  readonly notEnforced: boolean;
  readonly deferrable: DeferrableMode;
  readonly setWithNoCheck: (b: boolean) => void;
  readonly setNoInherit: (b: boolean) => void;
  readonly setNotEnforced: (b: boolean) => void;
  readonly setDeferrable: (m: DeferrableMode) => void;
}

export function CheckAddEngineFields(p: CheckAddEngineFieldsProps) {
  return (
    <>
      {p.showWithNoCheck ? (
        <label className="flex items-center gap-2 text-xs" data-testid={`${p.testId}-with-nocheck-label`}>
          <Checkbox
            checked={p.withNoCheck}
            onCheckedChange={(checked) => p.setWithNoCheck(checked === true)}
            data-testid={`${p.testId}-with-nocheck`}
          />
          <span>WITH NOCHECK — suppress legacy-data validation on creation</span>
        </label>
      ) : null}

      {p.showNoInherit ? (
        <label className="flex items-center gap-2 text-xs" data-testid={`${p.testId}-no-inherit-label`}>
          <Checkbox
            checked={p.noInherit}
            onCheckedChange={(checked) => p.setNoInherit(checked === true)}
            data-testid={`${p.testId}-no-inherit`}
          />
          <span>NO INHERIT — do not cascade to inheriting tables</span>
        </label>
      ) : null}

      {p.showNotEnforced ? (
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-xs" data-testid={`${p.testId}-not-enforced-label`}>
            <Checkbox
              checked={p.notEnforced}
              onCheckedChange={(checked) => p.setNotEnforced(checked === true)}
              data-testid={`${p.testId}-not-enforced`}
            />
            <span>NOT ENFORCED — parse but do not enforce (MySQL 8.0.16+)</span>
          </label>
          {p.mysqlVersionNote ? (
            <span className="text-[10px] text-muted-foreground" data-testid={`${p.testId}-version-note`}>
              {p.mysqlVersionNote}
            </span>
          ) : null}
        </div>
      ) : null}

      {p.showDeferrable ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs">Deferrable</legend>
          <RadioGroup
            name={`${p.testId}-deferrable`}
            value={p.deferrable}
            onValueChange={(v) => p.setDeferrable(v as DeferrableMode)}
            className="flex items-center gap-3 text-xs"
          >
            {(['unspecified', 'deferred', 'immediate'] as DeferrableMode[]).map((mode) => (
              <label key={mode} className="flex items-center gap-1">
                <RadioGroupItem value={mode} data-testid={`${p.testId}-deferrable-${mode}`} />
                <span className="capitalize">{mode === 'unspecified' ? 'Engine default' : mode}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      ) : null}
    </>
  );
}
